import type { MeshFlaws } from "@logic/lib/mesh"
import { extractMeshFromStore, findFlaws } from "@logic/lib/mesh"
import type { SetterOrUpdater } from "recoil"
import {
  atom,
  DefaultValue,
  selector,
  useRecoilState,
  useResetRecoilState,
  useSetRecoilState,
  useRecoilValue,
} from "recoil"

import {
  areaIndex,
  edgeIndex,
  findAreaState,
  findEdgeState,
  findNodeState,
  nodeIndex,
} from "./elements"

export const meshFlawsState = selector<MeshFlaws>({
  key: `invalidElementIds`,
  get: ({ get }) => {
    const nodeIds = get(nodeIndex)
    const edgeIds = get(edgeIndex)
    const mesh = extractMeshFromStore({
      nodeIds,
      edgeIds,
      getNode: (id) => get(findNodeState(id)),
      getEdge: (id) => get(findEdgeState(id)),
    })

    return findFlaws(mesh)
  },
})

export const flawedNodeIndex = selector<ReadonlyArray<string>>({
  key: `flawedNodeIndex`,
  get: ({ get }) => get(meshFlawsState).NODE,
})

export const flawedEdgeIndex = selector<ReadonlyArray<string>>({
  key: `flawedEdgeIndex`,
  get: ({ get }) => get(meshFlawsState).EDGE,
})

export const flawedAreaIndex = selector<ReadonlyArray<string>>({
  key: `flawedAreaIndex`,
  get: ({ get }) =>
    get(areaIndex).filter((id) => get(findAreaState(id)).azimuth === null),
})

export const holdFlawFocusState = atom<boolean>({
  key: `holdFlawFocus`,
  default: false,
})

export const focusedFlawIndex_INTERNAL = atom<string[]>({
  key: `focusedFlawIndex`,
  default: [],
})

export const focusedFlawIndexSoft = selector<string[]>({
  key: `focusedFlawIndexSoft`,
  get: ({ get }) => get(focusedFlawIndex_INTERNAL),
  set: ({ get, set }, newValue) => {
    if (!get(holdFlawFocusState)) {
      if (newValue instanceof DefaultValue) {
        set(focusedFlawIndex_INTERNAL, [])
      } else {
        set(focusedFlawIndex_INTERNAL, newValue)
      }
    }
  },
})

export const focusedFlawIndexHard = selector<string[]>({
  key: `focusedFlawIndexHard`,
  get: ({ get }) => get(focusedFlawIndex_INTERNAL),
  set: ({ get, set }, newValue) => {
    if (newValue instanceof DefaultValue) {
      set(focusedFlawIndex_INTERNAL, [])
      set(holdFlawFocusState, false)
    } else {
      set(focusedFlawIndex_INTERNAL, newValue)
      set(holdFlawFocusState, true)
    }
  },
})

export const useFocusedFlaws = (): {
  isHeld: boolean
  ids: string[]
  setHard: SetterOrUpdater<string[]>
  setSoft: SetterOrUpdater<string[]>
  resetHard: () => void
} => {
  const [ids, setSoft] = useRecoilState(focusedFlawIndexSoft)

  return {
    isHeld: useRecoilValue(holdFlawFocusState),
    ids,
    setSoft,
    setHard: useSetRecoilState(focusedFlawIndexHard),
    resetHard: useResetRecoilState(focusedFlawIndexHard),
  }
}
